import React, { useState, useEffect, useRef, MutableRefObject } from 'react';
import { debounce } from 'lodash';
import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import { TestId } from '@components/test-ids';
import { Theme, ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { boxShadowMixins } from '@styles/mixins';
import { ZIndex } from '@styles/z-index';
import { Tab } from '@mui/material';
import { mq } from '@styles/breakpoints';
import { Tabs } from '@styles/material-ui';

/* ***************** *
 *       Types       *
 * ***************** */

/* NOTE: if triggerRef is provided, the PageTabNavigation will show up
    when the trigger element is scrolled past and the show prop will be ignored */
export interface PageTabNavigationProps {
  theme?: Theme;
  testId?: string;
  anchorRefs: () => any;
  triggerRef?: () => any;
  show?: boolean;
}

/* ***************** *
 *       Styles      *
 * ***************** */
interface ContainerProps extends ThemeProps {
  show?: boolean;
}
const Container: any = styled.div(({ theme, show }: ContainerProps) => ({
  display: 'none',

  [mq.medium]: {
    position: 'fixed',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    top: 0,
    left: 0,
    width: '100vw',
    boxShadow: boxShadowMixins(theme.custom.colors.group10.base).default,
    backgroundColor: theme.custom.colors.white,
    zIndex: ZIndex.TAB_NAVIGATION,
    transition: `transform ${theme.custom.transitions.longSmooth}`,

    [mq.safari]: {
      boxShadow: 'none',
      borderBottom: `1px solid ${theme.custom.colors.group10.base}`
    },

    transform: `translateY(${show ? 0 : -theme.custom.contentSizes.tabNavHeight - theme.custom.spacing.xSmall}px)`
  }
}));

const TabNavContainer: any = styled.div(({ theme }: ThemeProps) => ({
  flex: '1 0 auto',
  width: '100%',
  maxWidth: theme.custom.contentSizes.maxContentWidth,
  padding: `0 ${theme.custom.spacing.large}px`
}));

/* ******************************** *
 *    PageTabNavigationComponent    *
 * ******************************** */

export const PageTabNavigationComponent = (props: PageTabNavigationProps) => {
  const t: any = useI18NextContext();
  const { anchorRefs, show, triggerRef, theme } = props;

  const getFirstAnchorRef = (anchors) => (Object.entries(anchors).find(([_key, val]: any) => !!val.current) || [])[0];

  /* *** STATE *** */
  const [currentTab, setCurrentTab] = useState<string | undefined>(getFirstAnchorRef(anchorRefs()));
  const [shouldRender, setShouldRender] = useState<boolean>(false);
  const [shouldShow, setShouldShow] = useState<boolean>(triggerRef ? false : show!);

  const currentTabRef = useRef(currentTab);

  /* *** HOOKS *** */
  useEffect(() => {
    // wait for the page to render to get all the Refs
    if (document) {
      if (!shouldRender) {
        const anchorsExist = Object.keys(anchorRefs()).find((label: string) => anchorRefs()[label].current);

        setShouldRender(anchorsExist ? true : false);
      }

      if (!currentTab) {
        setCurrentTab(getFirstAnchorRef(anchorRefs()));
      }
    }
  });

  useEffect(() => {
    currentTabRef.current = currentTab;
  }, [currentTab]);

  useEffect(() => {
    if (!triggerRef && show !== shouldShow) {
      setShouldShow(show!);
    }
  }, [show]);

  useEffect(() => {
    window.addEventListener('scroll', debouncedScrollHandler);

    return () => window.removeEventListener('scroll', debouncedScrollHandler);
  }, []);

  const sortAnchorRefs = (anchors) => {
    const anchorEntries: [string, MutableRefObject<null | HTMLElement>][] = Object.entries(anchors);
    const sortedAnchorEntries = anchorEntries
      .filter(([_key, val]: any) => !!val.current)
      .sort(([_, { current: currentA }], [__, { current: currentB }]) => {
        if (currentA?.compareDocumentPosition && currentB) {
          return currentA?.compareDocumentPosition(currentB) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
        }
        return 1;
      });
    return sortedAnchorEntries;
  };

  const handleSwitchTabsOnScroll = () => {
    let currentScrollTab = currentTab;

    if (triggerRef && triggerRef().current) {
      if (triggerRef().current.getBoundingClientRect().bottom < 0) {
        setShouldShow(true);
      } else {
        setShouldShow(false);
      }
    }

    const sortedAnchorRefs = sortAnchorRefs(anchorRefs());
    sortedAnchorRefs.forEach(([label, { current }]) => {
      if (
        current &&
        typeof current === 'object' &&
        current.getBoundingClientRect().top < theme!.custom.contentSizes.tabNavHeight + 10
      ) {
        currentScrollTab = label;
      }
    });

    if (currentScrollTab && currentScrollTab !== currentTabRef.current) {
      setCurrentTab(currentScrollTab);
    }
  };

  const debouncedScrollHandler = debounce(handleSwitchTabsOnScroll, 50);

  if (!shouldRender) {
    return null;
  }

  /* *** LOGIC *** */
  const handleChange = (_: any, newTab: string) => {
    if (!document) {
      return;
    }
    setCurrentTab(newTab);

    const anchorElement = anchorRefs()[newTab].current;

    if (anchorElement) {
      window.scrollTo({
        top: anchorElement.offsetTop - theme!.custom.contentSizes.tabNavHeight,
        behavior: 'smooth'
      });
    }
  };

  const renderTabs = () =>
    sortAnchorRefs(anchorRefs()).map(
      ([pageSection]) =>
        anchorRefs()[pageSection].current && (
          <Tab
            key={pageSection}
            label={t(pageSection)}
            value={pageSection}
            data-testid={TestId.pageTabNavigation.tab}
          />
        )
    );

  const tabs = renderTabs().filter((tab: any) => tab);

  return (
    <Container data-testid={props.testId || TestId.pageTabNavigation.main} show={shouldShow}>
      <TabNavContainer>
        <Tabs value={currentTab} onChange={handleChange} variant="fullWidth">
          {tabs}
        </Tabs>
      </TabNavContainer>
    </Container>
  );
};

PageTabNavigationComponent.defaultProps = {
  show: false
};

export const PageTabNavigation = withTheme(PageTabNavigationComponent);
